import { USE_MOCK } from '@/config'
import { createClient } from '@/api/supabase'

/**
 * Запись клиентской JS-ошибки в таблицу `public.client_errors` на SH.
 *
 * Источники: RootErrorBoundary (`kind: 'boundary'`), `window.onerror`
 * (`kind: 'error'`), `unhandledrejection` (`kind: 'promise'`).
 *
 * Функция намеренно «толстокожая»: глотает любые ошибки внутри и пишет в
 * консоль — её задача не помешать UI, а доставить отчёт «как получится».
 * Из-за RLS `actor_id` фактически проверяется по JWT и не подделать.
 */
export type ClientErrorKind = 'boundary' | 'error' | 'promise'

export interface ReportClientErrorInput {
  kind: ClientErrorKind
  message: string
  stack?: string | null
  componentStack?: string | null
  meta?: Record<string, unknown> | null
}

export async function reportClientError(input: ReportClientErrorInput): Promise<void> {
  if (USE_MOCK) return
  try {
    const supabase = createClient()
    const { data: sessionData } = await supabase.auth.getSession()
    const actorId = sessionData.session?.user?.id ?? null

    // app_version подставляется vite-define из package.json.
    const appVersion =
      typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : null

    const row = {
      actor_id: actorId,
      kind: input.kind,
      message: input.message.slice(0, 8000),
      stack: input.stack ? input.stack.slice(0, 16000) : null,
      component_stack: input.componentStack
        ? input.componentStack.slice(0, 8000)
        : null,
      url: typeof location !== 'undefined' ? location.href : null,
      user_agent:
        typeof navigator !== 'undefined' ? navigator.userAgent : null,
      app_version: appVersion,
      meta: input.meta ?? null,
    }

    const { error } = await supabase.from('client_errors').insert(row)
    if (error) {
      // Сюда попадаем при RLS-отказе (например, anon) или сетевой ошибке.
      // Не повышаем дальше — иначе onerror зациклится.
      console.warn('[client-errors] insert failed:', error.message)
    }
  } catch (e) {
    console.warn('[client-errors] report skipped:', e)
  }
}

export interface ClientErrorRow {
  id: string
  created_at: string
  actor_id: string | null
  actor_name: string | null
  kind: ClientErrorKind
  message: string
  stack: string | null
  component_stack: string | null
  url: string | null
  user_agent: string | null
  app_version: string | null
  meta: Record<string, unknown> | null
}

/**
 * Список последних ошибок для админ-страницы /admin/client-errors.
 * RLS пропускает SELECT только админов, поэтому дополнительной фильтрации
 * по ролям тут нет. FK на auth.users, поэтому имя пользователя тянем
 * вторым запросом по profiles (тот же двухшаговый подход, что в audit.ts).
 */
export async function listClientErrors(limit = 100): Promise<ClientErrorRow[]> {
  if (USE_MOCK) return []
  const supabase = createClient()
  const { data, error } = await supabase
    .from('client_errors')
    .select(
      'id, created_at, actor_id, kind, message, stack, component_stack, url, user_agent, app_version, meta',
    )
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) throw new Error(error.message)

  const rows = (data ?? []) as Array<Omit<ClientErrorRow, 'actor_name'>>

  const actorIds = Array.from(
    new Set(rows.map((r) => r.actor_id).filter((v): v is string => !!v)),
  )
  let nameById = new Map<string, string>()
  if (actorIds.length > 0) {
    const { data: profs, error: profErr } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', actorIds)
    if (profErr) throw new Error(profErr.message)
    nameById = new Map(
      (profs ?? []).map((p) => [
        p.id as string,
        (p.full_name as string | null) ?? '',
      ]),
    )
  }

  return rows.map((r) => ({
    ...r,
    actor_name: r.actor_id ? nameById.get(r.actor_id) ?? null : null,
  }))
}
